/**
 * Cora Upsell: Gift with Purchase (GWP)
 * Auto-adds/removes free gift products based on cart subtotal thresholds.
 * Gift items are identified by line item property _cora_gift: "true".
 * Anti-recursion: _gwpUpdating flag + source check + 500ms debounce.
 */
(function () {
  'use strict';

  const isDev =
    window.location.hostname === 'localhost' ||
    new URLSearchParams(window.location.search).has('preview_theme_id');

  window.CoraUpsell = window.CoraUpsell || {};

  if (customElements.get('cora-gift-with-purchase')) return;

  class CoraGiftWithPurchase extends HTMLElement {
    constructor() {
      super();
      this._cartUnsubscribe = null;
      this._gwpUpdating = false;
      this._debounceTimer = null;
      // Single-slot intentionally. When _gwpUpdating is true and another
      // cartUpdate event arrives, we overwrite this slot rather than queue.
      // We store the event only to know a re-evaluation is needed; we never
      // use event.cart for evaluation — always re-fetch /cart.js so we act
      // on actual server state, not a snapshot that may already be stale.
      this._latestPendingEvent = null;
      this._tiers = [];
    }

    connectedCallback() {
      this._parseTiers();
      if (this._tiers.length === 0) return;

      if (window.requestIdleCallback) {
        requestIdleCallback(() => this._init());
      } else {
        setTimeout(() => this._init(), 300);
      }
    }

    disconnectedCallback() {
      if (this._cartUnsubscribe) {
        this._cartUnsubscribe();
        this._cartUnsubscribe = null;
      }
      if (this._debounceTimer) {
        clearTimeout(this._debounceTimer);
      }
    }

    _parseTiers() {
      try {
        const raw = this.dataset.giftTiers;
        if (!raw) return;
        this._tiers = JSON.parse(raw)
          .filter((t) => t.enabled && t.variantId && t.threshold > 0)
          .sort((a, b) => b.threshold - a.threshold); // Highest threshold first
      } catch (_e) {
        this._tiers = [];
      }
    }

    _init() {
      if (!window.FoxTheme || !FoxTheme.pubsub) return;

      this._cartUnsubscribe = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        (event) => this._onCartUpdate(event)
      );

      // Initial evaluation
      this._loadAndEvaluate();
    }

    _onCartUpdate(event) {
      // Skip our own updates to prevent recursion
      if (event && event.source === 'cora-gwp') return;

      // Always capture the latest event so the debounce fires with fresh data.
      // If _gwpUpdating is true the in-flight operation's finally block calls
      // _refreshCart, which publishes cartUpdate and re-enters here once the
      // flag is cleared — so we don't need a timer for the in-flight case.
      this._latestPendingEvent = event;
      if (this._gwpUpdating) return;

      // Debounce to handle rapid cart changes. Always re-fetch /cart.js so
      // we evaluate against actual server state, not the event snapshot which
      // may be stale if multiple adds fired in quick succession.
      if (this._debounceTimer) clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => {
        this._latestPendingEvent = null;
        this._loadAndEvaluate();
      }, 500);
    }

    _loadAndEvaluate() {
      fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
        .then((r) => {
          if (!r.ok) throw new Error('Cart fetch failed');
          return r.json();
        })
        .then((cart) => this._evaluate(cart))
        .catch((e) => {
          if (isDev) console.error('[CoraGWP] _loadAndEvaluate failed:', e);
        });
    }

    _evaluate(cart) {
      if (!cart || !cart.items) return;

      const tierVariantIds = new Set(this._tiers.map((t) => parseInt(t.variantId, 10)));

      // Classify an item as a GWP gift only if it has the _cora_gift property,
      // OR if it's a tier variant whose final_line_price is zero. The price-zero
      // guard preserves the security defense (an attacker-added item bypasses
      // the property only when it's actually free), while paid purchases of a
      // variant that happens to be configured as a GWP gift pass through.
      // Without this, customers buying a product that's also a tier variant
      // had it silently removed at every cart update.
      const isGiftItem = (item) => {
        if (item.properties && item.properties._cora_gift === 'true') return true;
        if (tierVariantIds.has(item.variant_id) && item.final_line_price === 0) return true;
        return false;
      };

      // Calculate subtotal excluding gift items (in cents)
      let subtotal = 0;
      const giftLineKeys = [];
      const giftVariantIds = new Set();

      for (const item of cart.items) {
        if (isGiftItem(item)) {
          giftLineKeys.push(item.key);
          giftVariantIds.add(item.variant_id);
        } else {
          subtotal += item.final_line_price;
        }
      }

      const subtotalDollars = subtotal / 100;

      // Find the appropriate tier.
      // Compare in integer cents to avoid IEEE 754 precision loss at exact boundaries
      // (e.g. 3330 cents / 100 = 33.299... in float, which is < 33.3 threshold).
      let targetTier = null;
      for (const tier of this._tiers) {
        if (subtotal >= Math.round(tier.threshold * 100)) {
          targetTier = tier;
          break; // Tiers sorted highest first, so first match is best
        }
      }

      const targetVariantId = targetTier ? parseInt(targetTier.variantId, 10) : null;

      this._updateProgress(subtotalDollars, targetTier);

      // Remove gifts whose variant doesn't match the current target tier.
      // Paid items (non-zero price) are never classified as gifts above, so
      // they cannot end up in this list.
      const wrongGifts = [];
      for (const item of cart.items) {
        if (!isGiftItem(item)) continue;
        if (item.variant_id !== targetVariantId) {
          wrongGifts.push(item.key);
        }
      }

      const hasTargetGift = targetVariantId && giftVariantIds.has(targetVariantId);

      if (wrongGifts.length > 0) {
        this._removeGifts(wrongGifts, () => {
          if (targetVariantId && !hasTargetGift) {
            return this._addGift(targetVariantId);
          }
        });
      } else if (targetVariantId && !hasTargetGift) {
        this._addGift(targetVariantId);
      } else if (!targetVariantId && giftLineKeys.length > 0) {
        // Below all thresholds: remove every item already classified as a gift.
        this._removeGifts(giftLineKeys);
      }
    }

    _addGift(variantId) {
      this._gwpUpdating = true;

      return fetch('/cart/add.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
              properties: { _cora_gift: 'true' },
            },
          ],
        }),
      })
        .then((r) => {
          if (!r.ok) {
            return r
              .json()
              .then((err) => {
                const isOutOfStock =
                  r.status === 422 && JSON.stringify(err).toLowerCase().includes('inventory');
                if (window.CartDebug) console.warn('[CoraGWP] Gift add failed:', err);
                if (!isOutOfStock) throw new Error('Gift add failed');
                // Out of stock: refresh cart so UI is consistent, skip retry
                return this._refreshCart().catch((e) => {
                  if (isDev) {
                    console.error('[CoraGWP] _refreshCart (out-of-stock recovery) failed:', e);
                  }
                });
              })
              .catch((jsonErr) => {
                // Could not parse error JSON — treat as a hard failure
                if (!(jsonErr instanceof SyntaxError)) throw jsonErr;
                throw new Error('Gift add failed');
              });
          }
          const ct = r.headers.get('content-type') || '';
          if (!ct.includes('application/json') && !ct.includes('text/javascript')) {
            if (window.CartDebug) {
              console.warn('[CoraGWP] CF challenge on gift add, skipping refresh');
            }
            return;
          }
          return this._refreshCart();
        })
        .catch((e) => {
          if (isDev) console.error('[CoraGWP] _addGift outer catch:', e);
          this._refreshCart().catch((e2) => {
            if (isDev) console.error('[CoraGWP] _refreshCart (addGift recovery) failed:', e2);
          });
        })
        .finally(() => {
          this._gwpUpdating = false;
          // If a cart update arrived while we were busy, re-fetch and evaluate.
          if (this._latestPendingEvent) {
            this._latestPendingEvent = null;
            this._loadAndEvaluate();
          }
        });
    }

    _removeGifts(lineKeys, callback) {
      this._gwpUpdating = true;
      // Track whether the callback's own promise chain will handle flag cleanup.
      // If so, skip the finally block's cleanup to avoid overwriting _gwpUpdating
      // while the callback's in-flight operation is still running.
      let callbackHandlesPending = false;

      const updates = {};
      for (const key of lineKeys) {
        updates[key] = 0;
      }

      return fetch('/cart/update.json', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates }),
      })
        .then((r) => {
          if (!r.ok) throw new Error('Gift remove failed');
          if (callback) {
            callbackHandlesPending = true;
            return Promise.resolve(callback());
          }
          return this._refreshCart();
        })
        .catch((e) => {
          if (isDev) console.error('[CoraGWP] _removeGift outer catch:', e);
          this._refreshCart().catch((e2) => {
            if (isDev) console.error('[CoraGWP] _refreshCart (removeGift recovery) failed:', e2);
          });
        })
        .finally(() => {
          // If callback was invoked, its own finally (e.g. _addGift's) handles
          // _gwpUpdating and pending events — skip here to avoid a race.
          if (callbackHandlesPending) return;
          this._gwpUpdating = false;
          // If a cart update arrived while we were busy, re-fetch and evaluate.
          if (this._latestPendingEvent) {
            this._latestPendingEvent = null;
            this._loadAndEvaluate();
          }
        });
    }

    _refreshCart() {
      // Intentionally omits sections — cart.js's slow path (POST /cart/update.js)
      // fetches fresh section HTML protected by its _renderGeneration counter.
      // Bundling sections here would bypass that guard and publish stale HTML
      // whenever a user add is in flight concurrently with a GWP action.
      return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
        .then((r) => {
          if (!r.ok) throw new Error('Cart fetch failed');
          return r.json();
        })
        .then((cart) => {
          if (isDev && cart && cart.sections) {
            console.error(
              '[CoraGWP] _refreshCart: sections in cart payload — strip them before ' +
                'publishing; they trigger the cart.js fast path and bypass _renderGeneration'
            );
          }
          if (window.FoxTheme && FoxTheme.pubsub) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
              cart: cart,
              source: 'cora-gwp',
            });
          }
        });
    }

    _updateProgress(subtotalDollars, currentTier) {
      const progressEl = this.querySelector('.cora-gwp-progress');
      if (!progressEl) return;

      const textEl = progressEl.querySelector('.cora-gwp-progress__text');
      const barEl = progressEl.querySelector('.cora-gwp-progress__bar');

      if (!textEl || !barEl) return;

      // Find the next tier the customer hasn't reached yet
      let nextTier = null;
      const sortedAsc = [...this._tiers].reverse(); // lowest first
      for (const tier of sortedAsc) {
        // Compare in integer cents to avoid floating-point boundary errors
        if (Math.round(subtotalDollars * 100) < Math.round(tier.threshold * 100)) {
          nextTier = tier;
          break;
        }
      }

      if (currentTier) {
        // Customer qualifies for a gift
        textEl.textContent = 'You earned a free gift!';
        barEl.style.width = '100%';
      } else if (nextTier) {
        const remaining = (nextTier.threshold - subtotalDollars).toFixed(2);
        while (textEl.firstChild) textEl.removeChild(textEl.firstChild);
        textEl.appendChild(document.createTextNode('Spend '));
        const strong = document.createElement('strong');
        strong.textContent = '$' + remaining + ' more';
        textEl.appendChild(strong);
        textEl.appendChild(document.createTextNode(' to get a free gift!'));
        const pct = Math.min((subtotalDollars / nextTier.threshold) * 100, 99);
        barEl.style.width = pct + '%';
      } else {
        textEl.textContent = '';
        barEl.style.width = '0%';
      }
    }
  }

  customElements.define('cora-gift-with-purchase', CoraGiftWithPurchase);
})();
